import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { downloadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UploadCloud, FileDown, CheckCircle2 } from "lucide-react";

export default function ImportDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);

  const mut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/import-santri", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (data) => {
      setResult(data);
      qc.invalidateQueries({ queryKey: ["santri"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(`Sinkronisasi selesai: ${data.created} baru, ${data.updated} diperbarui`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal impor"),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setFile(null); setResult(null); } }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Sinkronisasi Data Santri (Spreadsheet)</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-500">Unggah file CSV atau Excel (.xlsx). Data dicocokkan berdasarkan <strong>NIS</strong> — yang sudah ada akan diperbarui, yang baru ditambahkan.</p>
          <Button variant="outline" className="gap-2 rounded-xl w-full" data-testid="download-template-button"
            onClick={() => downloadFile("/template-santri", "template_santri.csv")}>
            <FileDown className="h-4 w-4" /> Unduh Template CSV
          </Button>
          <label className="glass rounded-xl border-2 border-dashed border-emerald-200 p-6 flex flex-col items-center justify-center gap-2 cursor-pointer text-center" data-testid="import-dropzone">
            <UploadCloud className="h-8 w-8 text-emerald-500" />
            <span className="text-sm font-medium text-emerald-900">{file ? file.name : "Pilih file CSV / Excel"}</span>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="import-file-input"
              onChange={(e) => { setFile(e.target.files[0]); setResult(null); }} />
          </label>
          {result && (
            <div className="glass rounded-xl p-4 text-sm space-y-1" data-testid="import-result">
              <p className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> {result.created} santri baru, {result.updated} diperbarui</p>
              {result.errors?.length > 0 && <ul className="text-rose-500 text-xs list-disc pl-5">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
            </div>
          )}
          <Button data-testid="submit-import-button" onClick={() => mut.mutate()} disabled={!file || mut.isPending}
            className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800">{mut.isPending ? "Memproses..." : "Mulai Sinkronisasi"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
